import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { CloseIcon } from "./icons.js";

/**
 * 模态框。
 *
 * `createPortal` 到 `document.body`：手机壳布局里有多个 `overflow` 容器，
 * 放在组件树里会被祖先裁切，也和原型里那个绝对定位的弹窗不一致。
 *
 * 遮罩点击关闭、Esc 关闭、打开时锁 body 滚动 —— 这三件小事决定了它是
 * 「能用」还是「像 demo」。
 */
export function Modal({
  title,
  onClose,
  children,
  closeOnMask = true,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  closeOnMask?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return createPortal(
    <div
      className="modal-mask"
      onClick={closeOnMask ? onClose : undefined}
      role="presentation"
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="row-between">
          <h3>{title}</h3>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            aria-label="关闭"
          >
            <CloseIcon size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
